import { useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { ArrowBack, ContactPageOutlined, SupervisedUserCircleOutlined } from '@mui/icons-material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Skeleton, Stack, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import usePaginatedContacts from 'hooks/queries/usePaginatedContacts';
import useImports from 'hooks/queries/useImports';
import Button from 'domains/core/components/Button';
import EmptyStateLayout from 'domains/core/components/EmptyStateLayout';
import ExactSearchField from 'domains/core/components/ExactSearchField';
import { RotatingIconButton } from 'domains/core/components/IconButton';
import PageLayout from 'domains/core/components/PageLayout';
import TableEmptyState from 'domains/core/components/Table/TableEmptyState';
import ContactsUploadDialog from 'domains/contacts/components/ContactsUploadDialog';
import FileUploadButton from 'domains/contacts/components/FileUploadButton';
import useContactsFileUploadProps from 'domains/contacts/hooks/useContactsFileUploadProps';
import { SortDirection, URLPaths } from 'models/enums';
import { useAccessCentrePermissions } from 'hooks/useAccessCentrePermissions';
import ContactsTable from './ContactsTable';

const formatLastUpdatedTime = (imports: any) => {
    if (!imports || imports.length === 0) {
        return 'never';
    }
    const dateTime = DateTime.fromISO(imports[0].jobTimestamp);
    const datePart = dateTime.toFormat('MMM d, yyyy');
    const timePart = dateTime.toFormat('h:mma').toLowerCase();
    return `Updated on ${datePart} at ${timePart}`;
};

/**
 * The contacts home page for viewing and managing contacts.
 * This page has two different views:
 *  - Directly accessing the Contacts Page from the side bar (.../contacts)
 *  - Viewing an Segment from the visibility icon (eyeball icon) in the Segments Table (.../segments/contacts)
 * */
const ContactsPage = () => {
    const history = useHistory<any>();

    const route = history.location.pathname;
    const segmentId = history.location.state?.segmentId ?? null;
    const segmentName = history.location.state?.segmentName ?? '';

    // State variables so that the React Query refetch can be according to the proper sort params.
    const [sortColumnState, setSortColumnState] = useState('');
    const [sortDirectionState, setSortDirectionState] = useState(SortDirection.ASCENDING);
    const [searchInputValue, setSearchInputValue] = useState('');
    const [searchTerm, setSearchTerm] = useState('');

    // Pagination state
    const [page, setPage] = useState(1);
    const [contactsPerPage, setContactsPerPage] = useState(10);
    const offset = (page - 1) * contactsPerPage;

    const {
        data: contactsResponse,
        isLoading: isContactsLoading,
        isSuccess: isContactsSuccess,
        isError: isContactsError,
        isRefetching: isContactsRefetching,
        refetch: contactsRefetch,
        isFetching,
    } = usePaginatedContacts({
        options: {
            segmentId,
            sortColumn: sortColumnState,
            sortDirection: sortDirectionState,
            searchTerm,
            limit: contactsPerPage,
            offset,
        },
        reactQueryOptions: {
            keepPreviousData: true, // Prevent data from becoming undefined during page changes
        },
    });

    const contacts = contactsResponse?.data;
    const { data: imports, isLoading: isImportsLoading } = useImports();

    const fileUploadProps = useContactsFileUploadProps();
    const { canUploadContacts } = useAccessCentrePermissions();

    const isFromSegments = route === URLPaths.SEGMENTS_CONTACTS;
    const isContactsEmpty = !contacts?.length;

    useEffect(() => {
        if (isFromSegments && !segmentId && !segmentName) {
            history.push(URLPaths.SEGMENTS);
        }
    }, [history, isFromSegments, segmentId, segmentName]);

    // useEffect(() => {
    //     contactsRefetch();
    // }, [contactsRefetch, sortColumnState, sortDirectionState, searchTerm]);

    const onSort = (sortColumn: string, direction: SortDirection) => {
        setSortColumnState(sortColumn);
        setSortDirectionState(direction);
        setPage(1); // Reset to first page on sort change
    };

    const handleSearch = (value: string) => {
        setSearchTerm(value);
        setPage(1); // Reset to first page on search
    };

    // Pagination handlers
    const totalPages = Math.ceil(Math.min(contactsResponse?.total ?? 0, 1000) / contactsPerPage);

    const previousPage = () => {
        if (page > 1) {
            setPage((prev) => prev - 1);
        }
    };

    const nextPage = () => {
        if (page < totalPages) {
            setPage((prev) => prev + 1);
        }
    };

    const handleContactsPerPageChange = (newContactsPerPage: number) => {
        setContactsPerPage(newContactsPerPage);
        setPage(1); // Reset to first page when changing rows per page
    };

    const RefreshButton = (
        <Stack direction="row" alignItems="center">
            <Typography variant="body2">
                {isImportsLoading ? <Skeleton width={180} /> : formatLastUpdatedTime(imports)}
            </Typography>
            <RotatingIconButton
                aria-label="refresh"
                disabled={isContactsRefetching}
                isRefetching={isContactsRefetching}
                Icon={RefreshIcon}
                iconSx={{ fontSize: 20 }}
                handleClick={() => contactsRefetch()}
            />
        </Stack>
    );

    // Show error state if API fails
    if (isContactsError) {
        return (
            <EmptyStateLayout
                caption="Please try refreshing the page."
                header="There was a problem loading the contacts."
            />
        );
    }

    // Only show empty upload state if there's no search term and no contacts
    if (!isFromSegments && isContactsSuccess && isContactsEmpty && !searchTerm) {
        return (
            <EmptyStateLayout
                caption="contacts"
                header="let's get started"
                guidingText="Upload your contacts to begin engaging with your audience."
            >
                {canUploadContacts && (
                    <>
                        <ContactsUploadDialog {...fileUploadProps} />
                        <FileUploadButton {...fileUploadProps} />
                    </>
                )}
            </EmptyStateLayout>
        );
    }

    return (
        <PageLayout
            header={<Typography variant="h4">{!isFromSegments ? 'contacts' : segmentName}</Typography>}
            headerIcon={
                !isFromSegments ? (
                    <ContactPageOutlined fontSize="inherit" />
                ) : (
                    <SupervisedUserCircleOutlined fontSize="inherit" />
                )
            }
            headerAction={
                !isFromSegments ? (
                    <Stack direction="row" alignItems="center" columnGap={2}>
                        {RefreshButton}
                        {canUploadContacts && <FileUploadButton {...fileUploadProps} />}
                    </Stack>
                ) : (
                    <Button variant="text" startIcon={<ArrowBack />} onClick={() => history.push(URLPaths.SEGMENTS)}>
                        All Audiences
                    </Button>
                )
            }
            headerOthers={
                !isFromSegments && (
                    <ExactSearchField
                        placeholder="Search contacts..."
                        tooltipText="Search by first name, last name, email, or contact ID"
                        value={searchInputValue}
                        onChange={setSearchInputValue}
                        onSearch={handleSearch}
                        disabled={isContactsLoading || isContactsRefetching}
                        dataTestId="contacts-search"
                    />
                )
            }
        >
            <ContactsUploadDialog {...fileUploadProps} />
            {((isContactsLoading && !isContactsError) || (isContactsSuccess && !isContactsEmpty)) && (
                <ContactsTable
                    contacts={contacts ?? []}
                    totalContacts={contactsResponse?.total ?? 0}
                    onSort={onSort}
                    page={page}
                    totalPages={totalPages}
                    onPreviousPage={previousPage}
                    onNextPage={nextPage}
                    isFetching={isFetching}
                    contactsPerPage={contactsPerPage}
                    onContactsPerPageChange={handleContactsPerPageChange}
                />
            )}
            {isFromSegments && isContactsSuccess && isContactsEmpty && (
                <Typography>There are no contacts in this audience.</Typography>
            )}
            {!isFromSegments && isContactsSuccess && isContactsEmpty && searchTerm && (
                <TableEmptyState searchTerm={searchTerm} />
            )}
        </PageLayout>
    );
};

export default ContactsPage;
